import type { LegacyLogger } from "../../logger";
import type { RouteMatcher } from "./routeMatcher";
import type { QueryParameters, Router } from "./UseRouterContext";

export interface ParsedURL {
  query: Record<string, string>;
  pathname: string;
}

interface OdigeoNavigateOptions {
  isOF2: true;
  replace?: boolean;
}

export interface OdigeoRouterNavigate {
  (url: string, options?: OdigeoNavigateOptions): void;
}

type RouterMethod = "push" | "replace";

const urlRouteMap: Record<string, string> = {
  "": "homeflights",
  "/": "homeflights",
} as const;
const fixUrl = (url: string) => urlRouteMap[url] || url;

const buildPathname = (url: ParsedURL) => {
  const { query, pathname } = url;
  const formattedUrl = Object.entries(query).reduce(
    (acc, [key, value]) => acc.replace(`[${key}]`, value),
    pathname,
  );
  return fixUrl(formattedUrl);
};

const createRouterMethod =
  (
    odigeoNavigate: OdigeoRouterNavigate,
    method: RouterMethod,
    logger: LegacyLogger,
    options: OdigeoNavigateOptions,
  ) =>
  (url: string | ParsedURL) => {
    if (typeof url === "string") {
      logMessageIfUrlStartsWithSlash(logger, method, url);
      odigeoNavigate(fixUrl(url), options);
      return;
    }
    /**
     * @example https://nextjs.org/docs/api-reference/next/router#with-url-object
     */
    const formattedURL = buildPathname(url);
    logMessageIfUrlStartsWithSlash(logger, method, formattedURL);
    odigeoNavigate(formattedURL, options);
  };

const parseQueryString = (queryString: string | undefined = "") => {
  return queryString
    .replace(/^\?/, "")
    .split("&")
    .reduce(
      (memo, keyValue) => {
        const [key, ...rest] = decodeURIComponent(keyValue).split("=");
        if (!key) {
          return memo;
        }
        memo[key] = rest.join("=");
        return memo;
      },
      {} as Record<string, string>,
    );
};

const navigateBack = () => {
  window.history.back();
};

const logMessageIfUrlStartsWithSlash = (
  logger: LegacyLogger,
  method: RouterMethod,
  url: string,
) => {
  if (logger && url.startsWith("/"))
    logger.error(
      new Error("route starts with slash"),
      `The ${method} method has been used with a URL that starts with "/": ${url}`,
    );
};

interface CreateRouterParams {
  routeMatcher: RouteMatcher;
  odigeoNavigate: OdigeoRouterNavigate;
  logger: LegacyLogger;
  params?: Record<string, string>;
}

const EMPTY_OBJECT = {};

export const createRouter = <Query extends QueryParameters>({
  routeMatcher,
  odigeoNavigate,
  logger,
  params = EMPTY_OBJECT,
}: CreateRouterParams): Router<Query> => {
  const push = createRouterMethod(odigeoNavigate, "push", logger, {
    isOF2: true,
  });
  const replace = createRouterMethod(odigeoNavigate, "replace", logger, {
    isOF2: true,
    replace: true,
  });
  const pathname = window.location.hash.replace(/^#/, "");
  return {
    pathname,
    query: {
      ...parseQueryString(window.location.search),
      ...params,
      ...routeMatcher.getPathParams(pathname),
    } as Query,
    push,
    replace,
    back: navigateBack,
  };
};
